import { Children, cloneElement, isValidElement, useId, type ReactNode, type ReactElement } from 'react'
import { AlertCircle } from 'lucide-react'
import { cx } from './cx'
import s from './Field.module.css'

/**
 * A labelled form field.
 *
 * The label used to be a `<div>`, with the error in another `<div>` beside it
 * and nothing tying either to the control. That looks correct and is
 * inaccessible in three separate ways, all raised as UX-01 in the 2026-07-27
 * external review:
 *
 *   · clicking the label did not focus the control (no `htmlFor`);
 *   · a screen reader announced the control with no name, because nothing
 *     associated the text with it;
 *   · the validation error was never announced — it was visible text in an
 *     unrelated node.
 *
 * Now: a real `<label htmlFor>`, and the child control is cloned with the `id`,
 * `aria-describedby`, `aria-invalid` and `aria-required` it needs. Cloning
 * rather than a new API, so that every existing caller — dozens, across generic
 * CRUD, AFD and the bespoke desks — is fixed without being touched, and a caller
 * that already sets its own `id` keeps it.
 *
 * A field wrapping something other than one element (a radio group, a split
 * input) falls back to `role="group"` + `aria-labelledby`, which is the correct
 * pattern for a composite and still gives it an accessible name.
 *
 * ── ORIENTATION (#1680) ─────────────────────────────────────────────────────
 * `stack` (the default, and what every existing caller gets) is label above
 * control. `row` is a bounded label/help column beside a flexible control
 * column, which is the shape a SETTINGS row wants — and the shape the console's
 * settings page was hand-rolling in `Settings.module.css` with plain <div>s and
 * no label association at all, so every declared setting reached a screen
 * reader unnamed.
 *
 * The only structural difference is WHERE the help sits: under the label in
 * `row` (so it reads at a bounded measure beside its control) and under the
 * control in `stack`. The wiring is identical in both — the same `hint` id is
 * in the same `aria-describedby`, so moving the text does not move the meaning.
 */
export function Field({ label, tag, required, error, hint, children, htmlFor, orientation = 'stack' }: {
  label: string
  tag?: string | null
  required?: boolean
  error?: string
  /** guidance shown with the control, and announced with it */
  hint?: ReactNode
  children: ReactNode
  /** override the generated id — for a control that cannot accept a cloned one */
  htmlFor?: string
  /** `stack` = label above control; `row` = bounded label/help column beside it */
  orientation?: 'stack' | 'row'
}) {
  const auto = useId()
  const errorId = `${auto}-error`
  const hintId = `${auto}-hint`

  const only = Children.count(children) === 1 ? Children.only(children) : null
  const single = isValidElement(only) ? (only as ReactElement<Record<string, unknown>>) : null

  const controlId = htmlFor ?? (single?.props.id as string | undefined) ?? auto
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined

  const control = single
    ? cloneElement(single, {
        id: controlId,
        'aria-describedby':
          [single.props['aria-describedby'] as string | undefined, describedBy].filter(Boolean).join(' ') || undefined,
        'aria-invalid': error ? true : single.props['aria-invalid'],
        'aria-required': required ? true : single.props['aria-required'],
      })
    : children

  const hintNode = hint ? <div className={s.hint} id={hintId}>{hint}</div> : null
  /* role=alert so a validation failure is announced when it APPEARS, not only
     when the control is next focused.

     The GLYPH is required, not decoration (#2406 Spec 0 §8, compliance item
     20): "never colour alone". The message text already carries the meaning
     for a reader, but the thing marking this line as an ERROR rather than a
     hint was crimson and nothing else — invisible to anyone who cannot
     separate it from the muted hint above it, and gone entirely in greyscale.
     A circle, deliberately, not the `AlertTriangle` the kit uses for warnings:
     two states drawn with one glyph are one state as far as a reader is
     concerned. `aria-hidden` because `role="alert"` already announces the
     text, and a named icon would have it read twice. */
  const errorNode = error
    ? (
      <div className={s.error} id={errorId} role="alert">
        <AlertCircle className={s.errorGlyph} aria-hidden />
        <span>{error}</span>
      </div>
    )
    : null

  const body = (
    <>
      {control}
      {hintNode}
      {errorNode}
    </>
  )

  // The asterisk is decorative: aria-required on the control carries the
  // meaning, and a screen reader reading out "star" helps nobody.
  const labelContent = (
    <>
      {label}
      {required && <span className={s.req} aria-hidden="true">*</span>}
      {required && <span className={s.srOnly}> (required)</span>}
      {tag && <span className={s.tag}>{tag}</span>}
    </>
  )

  // A composite: name the GROUP rather than pointing one label at one of
  // several controls.
  const labelNode = single
    ? <label className={s.label} htmlFor={controlId}>{labelContent}</label>
    : <div className={s.label} id={`${auto}-label`}>{labelContent}</div>
  const groupAttrs = single ? {} : { role: 'group', 'aria-labelledby': `${auto}-label` }

  if (orientation === 'row') {
    return (
      <div className={cx(s.field, s.row)} {...groupAttrs}>
        <div className={s.labelCol}>{labelNode}{hintNode}</div>
        <div className={s.controlCol}>{control}{errorNode}</div>
      </div>
    )
  }

  return (
    <div className={s.field} {...groupAttrs}>
      {labelNode}
      {body}
    </div>
  )
}
